/**
 * "Mis cursos" (autoría): lista de cursos que el usuario crea, con estado
 * borrador/público. CREAR requiere cuenta (el sync fuerza la authz de autor).
 */
import { For, Show, type Component } from "solid-js";
import { msg } from "@lingui/core/macro";
import { BookMarked, Plus } from "lucide-solid";
import { createCourseMutation } from "@space/shared";
import { Link, useNavigate } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Mascot } from "@/components/ui/mascot";
import { useMyAuthoredCourses, useCourseWrite } from "../../hooks/use-course-data";

const VISIBILITY = {
  public: { label: msg`Public`, cls: "bg-joy-mint/15 text-joy-mint" },
  org: { label: msg`Academy`, cls: "bg-joy-sky/15 text-joy-sky" },
  draft: { label: msg`Draft`, cls: "bg-muted text-muted-foreground" },
} as const;

/** Narrows an arbitrary stored visibility value to a known key (falls back to draft). */
const visibilityKey = (v: unknown): keyof typeof VISIBILITY =>
  v === "public" || v === "org" ? v : "draft";

export const AuthoringList: Component = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { query } = useMyAuthoredCourses();
  const { drizzle, userId } = useCourseWrite();

  /**
   * ¿Está lista la capa de ESCRITURA local-first? El SQLite de PowerSync y la
   * sesión se resuelven de forma asíncrona y pueden tardar más que el shell:
   * hasta entonces `create` no puede hacer nada. Se expone como estado del
   * botón (deshabilitado + `aria-busy`) en vez de dejar que el clic caiga en un
   * `return` silencioso — un CTA que no hace NADA al pulsarlo es un bug de UX
   * (y hacía fallar academy-cohorts: el clic se perdía y no se creaba curso).
   */
  const ready = () => Boolean(drizzle()) && Boolean(userId());

  const create = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) return;
    const id = await createCourseMutation(db, uid, {});
    void navigate({ to: "/authoring/$courseId", params: { courseId: id } });
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <div class="space-mesh flex items-center justify-between rounded-3xl border border-border p-5 md:p-6">
        <div>
          <h1 class="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
            {t(msg`Authoring`)}
          </h1>
          <p class="text-sm text-muted-foreground">{t(msg`Create and publish courses.`)}</p>
        </div>
        <Button
          data-testid="create-course"
          onClick={create}
          disabled={!ready()}
          aria-busy={ready() ? undefined : "true"}
          class="gap-2 rounded-2xl font-bold shadow-md shadow-accent/25"
        >
          <Plus class="h-4 w-4" /> {t(msg`New course`)}
        </Button>
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 3 })}>
              {() => <Skeleton class="h-20 w-full rounded-2xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={(query.data?.length ?? 0) > 0}
          fallback={
            <div
              class="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border p-14 text-center"
              data-testid="authoring-empty"
            >
              <Mascot mood="happy" class="mb-4 h-24 w-24 animate-bob" />
              <h3 class="font-display text-lg font-bold">{t(msg`Create your first course`)}</h3>
              <p class="mt-1 text-sm text-muted-foreground">
                {t(msg`Start with "New course" and share what you know.`)}
              </p>
            </div>
          }
        >
          <div class="grid gap-3" data-testid="authoring-list">
            <For each={query.data}>
              {(c) => {
                const v = () => VISIBILITY[visibilityKey(c.visibility)];
                return (
                  <Link
                    to="/authoring/$courseId"
                    params={{ courseId: c.id }}
                    class="group flex items-center gap-4 rounded-2xl border border-border bg-surface p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md"
                    data-testid="authoring-course"
                  >
                    <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-sm transition-transform group-hover:scale-105">
                      <BookMarked class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="font-display font-bold">{c.title}</div>
                      <div class="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                        <span class={`rounded-full px-2 py-0.5 font-semibold ${v().cls}`}>
                          {t(v().label)}
                        </span>
                        <span>v{c.version ?? 1}</span>
                      </div>
                    </div>
                    <span class="text-accent opacity-0 transition-opacity group-hover:opacity-100">
                      {t(msg`Edit`)} →
                    </span>
                  </Link>
                );
              }}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
